import { Link, useSearchParams } from 'react-router-dom';
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useFraudHubTopRules } from '@/api/hooks/useFraudHubTopRules';
import { SEVERITY_BADGE_CLASS, SEVERITY_FILL } from '@/lib/severity';
import { parsePeriod } from '@/lib/timePeriod';
import type { Severity } from '@/types/alerts';
import type { TopRuleRow } from '@/types/fraudHub';

const NEUTRAL_FILL = 'hsl(215 20% 65%)';

/**
 * Section 4 of the Fraud Hub: six "rules misbehaving most" cards in a
 * 2-up (md) / 3-up (lg) grid.
 *
 * Each card is a <Link> to the Fraud Center pre-filtered by rule_id —
 * the natural one-click drill-deeper from "this rule is firing a lot
 * right now".
 *
 * The backend already orders rules severity-first then count-desc;
 * the SPA preserves that order strictly (no client-side sort).
 */
export function TopRulesPanel(): JSX.Element {
  const [searchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'), 'M');
  const { data, isLoading, isError, error } = useFraudHubTopRules(period);
  const periodLabel = data?.period_label ?? '';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Top Rules{periodLabel ? ` — ${periodLabel}` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading && <RulesSkeleton />}

        {!isLoading && isError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load top rules: {error?.message ?? 'unknown error'}
          </div>
        )}

        {!isLoading && !isError && data && data.rules.length === 0 && (
          <div className="flex h-[200px] flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
            <p className="font-medium">No rule activity in this period</p>
          </div>
        )}

        {!isLoading && !isError && data && data.rules.length > 0 && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            {data.rules.map((rule) => (
              <RuleCard key={rule.rule_id} rule={rule} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RulesSkeleton(): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-[120px] w-full" />
      ))}
    </div>
  );
}

function isKnownSeverity(s: string): s is Severity {
  return s === 'Critical' || s === 'High' || s === 'Medium'
      || s === 'Low'      || s === 'Watch' || s === 'Normal';
}

function RuleCard({ rule }: { rule: TopRuleRow }): JSX.Element {
  const sev = isKnownSeverity(rule.severity) ? rule.severity : null;
  const lineColor = sev ? SEVERITY_FILL[sev] : NEUTRAL_FILL;
  const badgeClass = sev ? SEVERITY_BADGE_CLASS[sev] : '';

  // Recharts wants object-shaped data points; map a flat number[] into
  // {i, v} so the LineChart can plot it.
  const sparkData = rule.daily_counts.map((v, i) => ({ i, v }));

  return (
    <Link
      to={`/fraud?rule_id=${encodeURIComponent(rule.rule_id)}`}
      className="block rounded-lg border bg-card p-3 transition-colors hover:bg-accent/40"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-xs text-muted-foreground">
          {rule.rule_id}
        </span>
        {sev && (
          <Badge
            variant="outline"
            className={cn('text-[10px] font-medium', badgeClass)}
          >
            {sev}
          </Badge>
        )}
      </div>
      <div
        className="mt-1 line-clamp-2 text-sm font-medium leading-snug"
        title={rule.rule_name ?? rule.rule_id}
      >
        {rule.rule_name ?? rule.rule_id}
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <div>
          <div className="text-xl font-bold tabular-nums leading-none">
            {rule.total_24h.toLocaleString('en-ZA')}
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            Fires in period
          </div>
        </div>
        <div className="h-10 w-20">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={sparkData}
              margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
            >
              <Line
                type="monotone"
                dataKey="v"
                stroke={lineColor}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Link>
  );
}

export default TopRulesPanel;
